import { ERROR_TOURNAMENT_UNREGISTER, GOT_TOURNAMENT_UNREGISTER } from 'constants/eventConstants';
import { decompressData } from 'utils/functions';

/**
 * EN-11257: Sit & Go tournaments with an unregister sent from the waiting
 * panel and not yet answered.
 *
 * Module scope, not component state, so leaving the table and reopening it
 * does not forget a request that may still be queued: the reopened panel
 * starts locked, because the engine's unregister is not idempotent. Every
 * reply settles it, including one that lands while no panel is mounted
 * (subscribeSitGoUnregisterSettlement, which App.tsx keeps mounted).
 *
 * A reply that never arrives anywhere releases the entry after
 * SIT_GO_UNREGISTER_RELEASE_MS. Two refunds need both requests inside the
 * engine at once, and it answers in well under a second, so after a minute
 * the first request is either lost or finished; a second one is then answered
 * "already unregistered" or goes through alone.
 */
export const SIT_GO_UNREGISTER_RELEASE_MS = 60_000;

// tournamentId -> when the request was sent (ms).
const unregistersInFlight = new Map<number, number>();

export const markSitGoUnregisterSent = (tournamentId: number) => {
  unregistersInFlight.set(tournamentId, Date.now());
};

/** How long until an unanswered request is released; 0 when none is held. */
export const sitGoUnregisterReleaseIn = (tournamentId: number): number => {
  const sentAt = unregistersInFlight.get(tournamentId);
  if (sentAt === undefined) return 0;
  const left = sentAt + SIT_GO_UNREGISTER_RELEASE_MS - Date.now();
  if (left > 0) return left;
  unregistersInFlight.delete(tournamentId);
  return 0;
};

export const isSitGoUnregisterInFlight = (tournamentId: number) =>
  sitGoUnregisterReleaseIn(tournamentId) > 0;

/** A reply (success or failure) arrived for this tournament's unregister. */
export const settleSitGoUnregister = (tournamentId: unknown) => {
  const id = Number(tournamentId);
  if (Number.isFinite(id)) unregistersInFlight.delete(id);
};

type SocketLike = {
  on: (event: string, handler: (raw: any) => void) => unknown;
  off: (event: string, handler: (raw: any) => void) => unknown;
};

/**
 * Settle every unregister reply, success or refusal, wherever the player is.
 * App.tsx keeps this mounted for the socket's life. Returns the unsubscribe,
 * which removes only these handlers: the panel listens to the same events.
 */
export const subscribeSitGoUnregisterSettlement = (socket: SocketLike) => {
  const settle = (raw: any) => settleSitGoUnregister(decompressData(raw)?.tournamentId);
  socket.on(GOT_TOURNAMENT_UNREGISTER, settle);
  socket.on(ERROR_TOURNAMENT_UNREGISTER, settle);
  return () => {
    socket.off(GOT_TOURNAMENT_UNREGISTER, settle);
    socket.off(ERROR_TOURNAMENT_UNREGISTER, settle);
  };
};

/** Test hook: forget every in-flight unregister. */
export const clearSitGoUnregistersInFlight = () => unregistersInFlight.clear();
